import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../components/SubscriptionAppCard';
import { Badge } from '../components/SubscriptionAppBadge';
import { Button } from '../components/SubscriptionAppButton';
import { StatCard, TrendIndicator } from '../components/StatCard';
import { DateRangePicker } from '../components/DateRangePicker';
import { SearchBar } from '../components/SearchBar';
import { TableFilter } from '../components/TableFilter';
import type { Filter } from '../components/TableFilter';
import { NavBar } from '../components/NavBar';
import { DropdownMenu } from '../components/SubscriptionAppDropdownMenu';
import { Package, Download, Plus, X, MoreVertical, Printer, Search, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown, ShoppingCart, RefreshCw } from 'lucide-react';
import { ExportButton } from '../components/ExportButton';
// Mock data for orders
const mockOrders = [{
  id: '1042',
  customerName: 'Sarah Johnson',
  customerEmail: 'sarah.j@example.com',
  type: 'Subscription',
  subscriptionId: 'SUB-23144',
  date: '2025-12-14T10:30:00',
  items: 6,
  total: 126.5,
  paymentStatus: 'Paid',
  fulfillmentStatus: 'Fulfilled'
}, {
  id: '1041',
  customerName: 'James Anderson',
  customerEmail: 'james.a@winery.com',
  type: 'Subscription',
  subscriptionId: 'SUB-23138',
  date: '2025-12-14T09:15:00',
  items: 6,
  total: 315.0,
  paymentStatus: 'In Retry',
  fulfillmentStatus: 'Unfulfilled',
  retryCount: 2,
  maxRetries: 4
}, {
  id: '1040',
  customerName: 'Michael Chen',
  customerEmail: 'demov18@winery.com',
  type: 'One-time',
  date: '2025-12-13T16:45:00',
  items: 3,
  total: 189.5,
  paymentStatus: 'Paid',
  fulfillmentStatus: 'Fulfilled'
}, {
  id: '1039',
  customerName: 'Emma Williams',
  customerEmail: 'demov211@winery.com',
  type: 'Subscription',
  subscriptionId: 'SUB-23139',
  date: '2025-12-13T14:20:00',
  items: 6,
  total: 245.0,
  paymentStatus: 'Failed',
  fulfillmentStatus: 'Unfulfilled'
}, {
  id: '1038',
  customerName: 'Ethan White',
  customerEmail: 'ethan.w@winery.com',
  type: 'Subscription',
  subscriptionId: 'SUB-23130',
  date: '2025-12-12T11:30:00',
  items: 12,
  total: 298.75,
  paymentStatus: 'In Retry',
  fulfillmentStatus: 'Unfulfilled',
  retryCount: 3,
  maxRetries: 4
}, {
  id: '1037',
  customerName: 'Olivia Martinez',
  customerEmail: 'olivia.m@winery.com',
  type: 'Subscription',
  subscriptionId: 'SUB-23137',
  date: '2025-12-12T10:00:00',
  items: 12,
  total: 98.75,
  paymentStatus: 'Paid',
  fulfillmentStatus: 'Fulfilled'
}, {
  id: '1036',
  customerName: 'William Taylor',
  customerEmail: 'w.taylor@winery.com',
  type: 'One-time',
  date: '2025-12-11T15:30:00',
  items: 12,
  total: 275.5,
  paymentStatus: 'Failed',
  fulfillmentStatus: 'Unfulfilled'
}, {
  id: '1035',
  customerName: 'Sophia Brown',
  customerEmail: 'sophia.b@winery.com',
  type: 'Subscription',
  subscriptionId: 'SUB-23135',
  date: '2025-12-11T09:45:00',
  items: 3,
  total: 142.0,
  paymentStatus: 'Paid',
  fulfillmentStatus: 'Fulfilled'
}, {
  id: '1034',
  customerName: 'Victoria Moore',
  customerEmail: 'victoria.m@winery.com',
  type: 'Subscription',
  subscriptionId: 'SUB-23121',
  date: '2025-12-10T13:20:00',
  items: 6,
  total: 195.0,
  paymentStatus: 'In Retry',
  fulfillmentStatus: 'Unfulfilled',
  retryCount: 1,
  maxRetries: 4
}, {
  id: '1033',
  customerName: 'Daniel Scott',
  customerEmail: 'daniel.s@winery.com',
  type: 'Subscription',
  subscriptionId: 'SUB-23120',
  date: '2025-12-10T11:00:00',
  items: 12,
  total: 285.5,
  paymentStatus: 'Paid',
  fulfillmentStatus: 'Fulfilled'
}];
export function OrdersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // State
  const [isShopifySynced, setIsShopifySynced] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [dateRange, setDateRange] = useState('Last 30 days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage_, setCurrentPage_] = useState(1);
  const pageSize = 10;
  // Stats date range state
  const [statsTimeRange, setStatsTimeRange] = useState('Last 30 Days');
  const statsTimeOptions = ['Last 7 Days', 'Last 30 Days', 'Last 60 Days', 'Last 90 Days'] as const;
  // Filter state
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({
    status: [],
    orderType: [],
    paymentStatus: [],
    fulfillmentStatus: [],
    customer: [],
    orderId: [],
    date: [],
    items: [],
    total: []
  });
  const [statusFilter, setStatusFilter] = useState('All');
  const [orderTypeFilter, setOrderTypeFilter] = useState('All');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('All');
  const [addedFilters, setAddedFilters] = useState<Set<string>>(new Set());
  const [openFilterDropdowns, setOpenFilterDropdowns] = useState<Set<string>>(new Set());
  const [showAddFilterDropdown, setShowAddFilterDropdown] = useState(false);
  // Custom date ranges state
  const [customDateRanges, setCustomDateRanges] = useState<Record<string, {
    startDate: string;
    endDate: string;
  }>>({});
  // State for managing which order's dropdown is open
  const [openOrderDropdown, setOpenOrderDropdown] = useState<string | null>(null);
  // Sort state
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({
    key: 'date',
    direction: 'desc'
  });
  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  // Calculate stats
  const stats = useMemo(() => {
    const now = new Date();
    const getDaysForRange = (range: string) => {
      switch (range) {
        case 'Last 7 Days':
          return 7;
        case 'Last 30 Days':
          return 30;
        case 'Last 60 Days':
          return 60;
        case 'Last 90 Days':
          return 90;
        default:
          return 30;
      }
    };
    const days = getDaysForRange(statsTimeRange);
    const filteredOrders = mockOrders.filter(order => {
      const orderDate = new Date(order.date);
      const daysDiff = Math.floor((now.getTime() - orderDate.getTime()) / (1000 * 60 * 60 * 24));
      return daysDiff >= 0 && daysDiff <= days;
    });
    const totalOrders = filteredOrders.length;
    const totalRevenue = filteredOrders.reduce((sum, order) => sum + order.total, 0);
    const paymentFailed = filteredOrders.filter(o => o.paymentStatus === 'Failed').length;
    const pendingFulfillment = filteredOrders.filter(o => o.fulfillmentStatus === 'Unfulfilled').length;
    return {
      totalOrders,
      totalRevenue,
      paymentFailed,
      pendingFulfillment
    };
  }, [statsTimeRange]);
  // Available filters
  const availableFilters: Filter[] = [{
    id: 'status',
    label: 'Status',
    options: [{
      id: '1',
      label: 'Pending',
      value: 'pending'
    }, {
      id: '2',
      label: 'Processing',
      value: 'processing'
    }, {
      id: '3',
      label: 'Completed',
      value: 'completed'
    }, {
      id: '4',
      label: 'Failed',
      value: 'failed'
    }, {
      id: '5',
      label: 'Cancelled',
      value: 'cancelled'
    }, {
      id: '6',
      label: 'Refunded',
      value: 'refunded'
    }],
    selectedValues: filterValues.status || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('status')
  }, {
    id: 'orderId',
    label: 'Order #',
    options: [],
    selectedValue: filterValues.orderId?.[0] || '',
    type: 'search',
    isOpen: openFilterDropdowns.has('orderId')
  }, {
    id: 'customer',
    label: 'Customer',
    options: [{
      id: '1',
      label: 'A-F',
      value: 'a-f'
    }, {
      id: '2',
      label: 'G-M',
      value: 'g-m'
    }, {
      id: '3',
      label: 'N-S',
      value: 'n-s'
    }, {
      id: '4',
      label: 'T-Z',
      value: 't-z'
    }],
    selectedValues: filterValues.customer || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('customer')
  }, {
    id: 'orderType',
    label: 'Type',
    options: [{
      id: '1',
      label: 'Subscription',
      value: 'subscription'
    }, {
      id: '2',
      label: 'One-time',
      value: 'one-time'
    }],
    selectedValues: filterValues.orderType || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('orderType')
  }, {
    id: 'date',
    label: 'Date',
    options: [{
      id: '1',
      label: 'Today',
      value: 'today'
    }, {
      id: '2',
      label: 'Last 7 days',
      value: 'last-7-days'
    }, {
      id: '3',
      label: 'Last 30 days',
      value: 'last-30-days'
    }, {
      id: '4',
      label: 'Last 3 months',
      value: 'last-3-months'
    }, {
      id: '5',
      label: 'This month',
      value: 'this-month'
    }, {
      id: '6',
      label: 'Last month',
      value: 'last-month'
    }, {
      id: '7',
      label: 'Custom',
      value: 'custom'
    }],
    selectedValues: filterValues.date || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('date'),
    customDateRange: customDateRanges['date']
  }, {
    id: 'items',
    label: 'Items',
    options: [{
      id: '1',
      label: '1-3 items',
      value: '1-3'
    }, {
      id: '2',
      label: '4-6 items',
      value: '4-6'
    }, {
      id: '3',
      label: '7-12 items',
      value: '7-12'
    }, {
      id: '4',
      label: '12+ items',
      value: '12-plus'
    }],
    selectedValues: filterValues.items || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('items')
  }, {
    id: 'total',
    label: 'Total',
    options: [{
      id: '1',
      label: 'Under $100',
      value: 'under-100'
    }, {
      id: '2',
      label: '$100 - $200',
      value: '100-200'
    }, {
      id: '3',
      label: '$200 - $300',
      value: '200-300'
    }, {
      id: '4',
      label: 'Over $300',
      value: 'over-300'
    }],
    selectedValues: filterValues.total || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('total')
  }, {
    id: 'paymentStatus',
    label: 'Payment',
    options: [{
      id: '1',
      label: 'Paid',
      value: 'paid'
    }, {
      id: '2',
      label: 'In Retry',
      value: 'in-retry'
    }, {
      id: '3',
      label: 'Failed',
      value: 'failed'
    }, {
      id: '4',
      label: 'Refunded',
      value: 'refunded'
    }, {
      id: '5',
      label: 'Pending',
      value: 'pending'
    }],
    selectedValues: filterValues.paymentStatus || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('paymentStatus')
  }, {
    id: 'fulfillmentStatus',
    label: 'Fulfillment',
    options: [{
      id: '1',
      label: 'Fulfilled',
      value: 'fulfilled'
    }, {
      id: '2',
      label: 'Unfulfilled',
      value: 'unfulfilled'
    }, {
      id: '3',
      label: 'Partially Fulfilled',
      value: 'partially'
    }],
    selectedValues: filterValues.fulfillmentStatus || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('fulfillmentStatus')
  }];
  // Filter and sort orders
  const filteredOrders = useMemo(() => {
    let filtered = mockOrders.filter(order => {
      if (filterValues.status && filterValues.status.length > 0) {
        const statusMatch = filterValues.status.some(value => {
          if (value === 'pending') return order.fulfillmentStatus === 'Unfulfilled';
          if (value === 'processing') return order.fulfillmentStatus === 'Partially';
          if (value === 'completed') return order.fulfillmentStatus === 'Fulfilled';
          if (value === 'failed') return order.paymentStatus === 'Failed';
          return false;
        });
        if (!statusMatch) return false;
      }
      if (filterValues.orderId && filterValues.orderId.length > 0 && filterValues.orderId[0].trim()) {
        const searchValue = filterValues.orderId[0].toLowerCase();
        if (!order.id.toLowerCase().includes(searchValue)) return false;
      }
      if (filterValues.customer && filterValues.customer.length > 0) {
        const firstLetter = order.customerName.charAt(0).toLowerCase();
        const customerMatch = filterValues.customer.some(value => {
          if (value === 'a-f') return firstLetter >= 'a' && firstLetter <= 'f';
          if (value === 'g-m') return firstLetter >= 'g' && firstLetter <= 'm';
          if (value === 'n-s') return firstLetter >= 'n' && firstLetter <= 's';
          if (value === 't-z') return firstLetter >= 't' && firstLetter <= 'z';
          return false;
        });
        if (!customerMatch) return false;
      }
      if (filterValues.orderType && filterValues.orderType.length > 0) {
        const typeMatch = filterValues.orderType.some(value => {
          if (value === 'subscription') return order.type === 'Subscription';
          if (value === 'one-time') return order.type === 'One-time';
          return false;
        });
        if (!typeMatch) return false;
      }
      if (filterValues.date && filterValues.date.length > 0) {
        const orderDate = new Date(order.date);
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - orderDate.getTime()) / (1000 * 60 * 60 * 24));
        const dateMatch = filterValues.date.some(value => {
          if (value === 'today') return daysDiff === 0;
          if (value === 'last-7-days') return daysDiff >= 0 && daysDiff <= 7;
          if (value === 'last-30-days') return daysDiff >= 0 && daysDiff <= 30;
          if (value === 'last-3-months') return daysDiff >= 0 && daysDiff <= 90;
          if (value === 'this-month') {
            return orderDate.getMonth() === now.getMonth() && orderDate.getFullYear() === now.getFullYear();
          }
          if (value === 'last-month') {
            const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            return orderDate.getMonth() === lastMonth.getMonth() && orderDate.getFullYear() === lastMonth.getFullYear();
          }
          if (value === 'custom') {
            const dateRange = customDateRanges['date'];
            if (dateRange && dateRange.startDate && dateRange.endDate) {
              const start = new Date(dateRange.startDate);
              const end = new Date(dateRange.endDate);
              end.setHours(23, 59, 59, 999);
              return orderDate >= start && orderDate <= end;
            }
            return true;
          }
          return false;
        });
        if (!dateMatch) return false;
      }
      if (filterValues.items && filterValues.items.length > 0) {
        const itemsMatch = filterValues.items.some(value => {
          if (value === '1-3') return order.items >= 1 && order.items <= 3;
          if (value === '4-6') return order.items >= 4 && order.items <= 6;
          if (value === '7-12') return order.items >= 7 && order.items <= 12;
          if (value === '12-plus') return order.items > 12;
          return false;
        });
        if (!itemsMatch) return false;
      }
      if (filterValues.total && filterValues.total.length > 0) {
        const totalMatch = filterValues.total.some(value => {
          if (value === 'under-100') return order.total < 100;
          if (value === '100-200') return order.total >= 100 && order.total < 200;
          if (value === '200-300') return order.total >= 200 && order.total < 300;
          if (value === 'over-300') return order.total >= 300;
          return false;
        });
        if (!totalMatch) return false;
      }
      if (filterValues.paymentStatus && filterValues.paymentStatus.length > 0) {
        const paymentMatch = filterValues.paymentStatus.some(value => {
          if (value === 'in-retry') return order.paymentStatus === 'In Retry';
          return order.paymentStatus.toLowerCase() === value.toLowerCase();
        });
        if (!paymentMatch) return false;
      }
      if (filterValues.fulfillmentStatus && filterValues.fulfillmentStatus.length > 0) {
        const fulfillmentMatch = filterValues.fulfillmentStatus.some(value => {
          if (value === 'fulfilled') return order.fulfillmentStatus === 'Fulfilled';
          if (value === 'unfulfilled') return order.fulfillmentStatus === 'Unfulfilled';
          if (value === 'partially') return order.fulfillmentStatus === 'Partially';
          return false;
        });
        if (!fulfillmentMatch) return false;
      }
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        return order.id.toLowerCase().includes(query) || order.customerName.toLowerCase().includes(query) || order.customerEmail.toLowerCase().includes(query);
      }
      return true;
    });
    if (sortConfig) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: any;
        let bValue: any;
        switch (sortConfig.key) {
          case 'id':
            aValue = parseInt(a.id);
            bValue = parseInt(b.id);
            break;
          case 'customer':
            aValue = a.customerName.toLowerCase();
            bValue = b.customerName.toLowerCase();
            break;
          case 'type':
            aValue = a.type;
            bValue = b.type;
            break;
          case 'date':
            aValue = new Date(a.date).getTime();
            bValue = new Date(b.date).getTime();
            break;
          case 'items':
            aValue = a.items;
            bValue = b.items;
            break;
          case 'total':
            aValue = a.total;
            bValue = b.total;
            break;
          case 'paymentStatus':
            aValue = a.paymentStatus;
            bValue = b.paymentStatus;
            break;
          case 'fulfillmentStatus':
            aValue = a.fulfillmentStatus;
            bValue = b.fulfillmentStatus;
            break;
          default:
            return 0;
        }
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [filterValues, searchQuery, customDateRanges, sortConfig]);
  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / pageSize);
  const paginatedOrders = filteredOrders.slice((currentPage_ - 1) * pageSize, currentPage_ * pageSize);
  const hasActiveFilters = addedFilters.size > 0 || searchQuery.trim() !== '';
  const clearAllFilters = () => {
    setFilterValues({
      status: [],
      orderType: [],
      paymentStatus: [],
      fulfillmentStatus: [],
      customer: [],
      orderId: [],
      date: [],
      items: [],
      total: []
    });
    setAddedFilters(new Set());
    setSearchQuery('');
  };
  const getPaymentStatusVariant = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    if (status === 'Paid') return 'success';
    if (status === 'In Retry') return 'warning';
    if (status === 'Failed') return 'error';
    if (status === 'Pending') return 'default';
    if (status === 'Refunded') return 'default';
    return 'default';
  };
  const getFulfillmentStatusVariant = (status: string): 'success' | 'warning' | 'info' | 'default' => {
    if (status === 'Fulfilled') return 'success';
    if (status === 'Unfulfilled') return 'warning';
    if (status === 'Partially') return 'info';
    return 'default';
  };
  const handleToggleAddFilterDropdown = () => {
    setShowAddFilterDropdown(!showAddFilterDropdown);
  };
  const handleAddFilter = (filterId: string) => {
    const newAddedFilters = new Set(addedFilters);
    newAddedFilters.add(filterId);
    setAddedFilters(newAddedFilters);
    switch (filterId) {
      case 'status':
        setStatusFilter('All');
        break;
      case 'orderType':
        setOrderTypeFilter('All');
        break;
      case 'paymentStatus':
        setPaymentStatusFilter('All');
        break;
    }
    setShowAddFilterDropdown(false);
  };
  const handleClearAll = () => {
    setFilterValues({
      status: [],
      orderType: [],
      paymentStatus: [],
      fulfillmentStatus: [],
      customer: [],
      orderId: [],
      date: [],
      items: [],
      total: []
    });
    setAddedFilters(new Set());
    setSearchQuery('');
  };
  const handleFilterChange = (filterId: string, value: string | string[]) => {
    const newFilterValues = {
      ...filterValues,
      [filterId]: Array.isArray(value) ? value : [value]
    };
    setFilterValues(newFilterValues);
  };
  const handleFilterRemove = (filterId: string) => {
    const newAddedFilters = new Set(addedFilters);
    newAddedFilters.delete(filterId);
    setAddedFilters(newAddedFilters);
    switch (filterId) {
      case 'status':
        setStatusFilter('All');
        break;
      case 'orderType':
        setOrderTypeFilter('All');
        break;
      case 'paymentStatus':
        setPaymentStatusFilter('All');
        break;
    }
  };
  const handleToggleFilterDropdown = (filterId: string) => {
    const newOpenFilterDropdowns = new Set(openFilterDropdowns);
    newOpenFilterDropdowns.add(filterId);
    setOpenFilterDropdowns(newOpenFilterDropdowns);
  };
  const handleCustomDateChange = (filterId: string, startDate: string, endDate: string) => {
    setCustomDateRanges(prev => ({
      ...prev,
      [filterId]: {
        startDate,
        endDate
      }
    }));
  };
  const handleTotalOrdersClick = () => {
    let filterValue = 'last-30-days';
    if (statsTimeRange === 'Last 7 Days') filterValue = 'last-7-days';else if (statsTimeRange === 'Last 30 Days') filterValue = 'last-30-days';else if (statsTimeRange === 'Last 60 Days' || statsTimeRange === 'Last 90 Days') filterValue = 'last-3-months';
    setFilterValues({
      status: [],
      orderType: [],
      paymentStatus: [],
      fulfillmentStatus: [],
      customer: [],
      orderId: [],
      date: [filterValue],
      items: [],
      total: []
    });
    setAddedFilters(new Set(['date']));
  };
  const handleRevenueClick = () => {
    let filterValue = 'last-30-days';
    if (statsTimeRange === 'Last 7 Days') filterValue = 'last-7-days';else if (statsTimeRange === 'Last 30 Days') filterValue = 'last-30-days';else if (statsTimeRange === 'Last 60 Days' || statsTimeRange === 'Last 90 Days') filterValue = 'last-3-months';
    setFilterValues({
      status: [],
      orderType: [],
      paymentStatus: [],
      fulfillmentStatus: [],
      customer: [],
      orderId: [],
      date: [filterValue],
      items: [],
      total: []
    });
    setAddedFilters(new Set(['date']));
  };
  const handlePaymentFailedClick = () => {
    setFilterValues({
      status: [],
      orderType: [],
      paymentStatus: ['failed'],
      fulfillmentStatus: [],
      customer: [],
      orderId: [],
      date: [],
      items: [],
      total: []
    });
    setAddedFilters(new Set(['paymentStatus']));
  };
  // Sync progress simulation
  useEffect(() => {
    if (!isSyncing) return;
    setSyncProgress(0);
    const interval = setInterval(() => {
      setSyncProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setIsSyncing(false);
            setIsShopifySynced(true);
          }, 400);
          return 100;
        }
        // Simulate realistic progress: fast start, slow middle, fast finish
        if (prev < 30) return prev + Math.random() * 8 + 4;
        if (prev < 70) return prev + Math.random() * 4 + 1;
        if (prev < 90) return prev + Math.random() * 6 + 2;
        return prev + Math.random() * 3 + 1;
      });
    }, 200);
    return () => clearInterval(interval);
  }, [isSyncing]);
  useEffect(() => {
    if (location.state?.filterValues) {
      setFilterValues(prev => ({
        ...prev,
        ...location.state.filterValues
      }));
      const filterKeys = Object.keys(location.state.filterValues);
      if (filterKeys.length > 0) {
        setAddedFilters(new Set(filterKeys));
      }
    }
  }, [location.state]);
  const handleViewOrderInShopify = (orderId: string) => {
    console.log(`View order ${orderId} in Shopify`);
    setOpenOrderDropdown(null);
  };
  const handleSort = (columnKey: string) => {
    setSortConfig(current => {
      if (!current || current.key !== columnKey) {
        return {
          key: columnKey,
          direction: 'asc'
        };
      }
      if (current.direction === 'asc') {
        return {
          key: columnKey,
          direction: 'desc'
        };
      }
      return null;
    });
  };
  return <div className="bg-[#F1F5F9] min-h-full p-4 md:p-6" data-id="element-11785" data-mp="o17bf8j4a99s">
      {/* NavBar */}
      <div className="mb-4 md:mb-6" data-id="element-11786" data-mp="9cb63j8g3acv">
        <NavBar tabs={[]} title="Orders" data-id="element-11787" data-mp="e9bdpyky0l63" />
      </div>

      {!isShopifySynced ? isSyncing ? <div className="flex flex-col items-center justify-center min-h-[60vh] p-6" data-id="element-11788" data-mp="11z5q6f1co7pt">
            <div className="max-w-md text-center w-full" data-id="element-11789" data-mp="11xb6f41telnb">
              <div className="mb-6 flex justify-center" data-id="element-11790" data-mp="16wxjndhn81w4">
                <div className="w-20 h-20 rounded-full bg-[#E0F2FE] flex items-center justify-center" data-id="element-11791" data-mp="s1833uv88gys">
                  <RefreshCw className="w-10 h-10 text-[#0EA5E9] animate-spin" data-id="element-11792" data-mp="wzswbac2a0dq" />
                </div>
              </div>
              <h1 className="text-[24px] font-semibold text-[#0F172A] mb-3" data-id="element-11793" data-mp="3cq5l6tqljyzm">
                Syncing Orders…
              </h1>
              <p className="text-[14px] text-[#64748B] mb-8 leading-relaxed" data-id="element-11794" data-mp="3hruv262326m4">
                We're importing your orders from Shopify. This may take a
                moment.
              </p>

              <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm mb-6" data-id="element-11795" data-mp="1j8zvfny5awu3">
                {/* Progress bar */}
                <div className="mb-3" data-id="element-11796" data-mp="1efeylz8ag8wh">
                  <div className="flex items-center justify-between mb-2" data-id="element-11797" data-mp="19hn4cyk68ap4">
                    <span className="text-[13px] font-medium text-[#475569]" data-id="element-11798" data-mp="14jbx8vl9geal">
                      Import progress
                    </span>
                    <span className="text-[13px] font-semibold text-[#0EA5E9]" data-id="element-11799" data-mp="10gf6uiphbdw3">
                      {Math.round(syncProgress)}%
                    </span>
                  </div>
                  <div className="w-full h-2.5 bg-[#E2E8F0] rounded-full overflow-hidden" data-id="element-11800" data-mp="qkubv4itvdvu">
                    <div className="h-full bg-[#0EA5E9] rounded-full transition-all duration-200 ease-out" style={{
                width: `${Math.min(syncProgress, 100)}%`
              }} data-id="element-11801" data-mp="2px6kk04oabe5" />
                  </div>
                </div>

                {/* Status steps */}
                <div className="space-y-2.5 mt-5" data-id="element-11802" data-mp="2xg60twwu7mta">
                  <div className="flex items-center gap-2.5" data-id="element-11803" data-mp="2xeuw9uvkib14">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${syncProgress >= 15 ? 'bg-[#10B981]' : 'bg-[#E2E8F0]'}`} data-id="element-11804" data-mp="326mglno4rkit">
                      {syncProgress >= 15 && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-11805" data-mp="29iqkol6dxwn3">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-11806" data-mp="2eimres6gw26c" />
                        </svg>}
                    </div>
                    <span className={`text-[13px] ${syncProgress >= 15 ? 'text-[#0F172A] font-medium' : 'text-[#94A3B8]'}`} data-id="element-11807" data-mp="2ofrd6qwehvmm">
                      Connecting to Shopify
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5" data-id="element-11808" data-mp="3p0noailfpcq">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${syncProgress >= 50 ? 'bg-[#10B981]' : syncProgress >= 15 ? 'bg-[#E0F2FE] border-2 border-[#0EA5E9]' : 'bg-[#E2E8F0]'}`} data-id="element-11809" data-mp="3rmsw3i0hsrs">
                      {syncProgress >= 50 && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-11810" data-mp="yjnroddre15m">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-11811" data-mp="trcon1nq1z8u" />
                        </svg>}
                    </div>
                    <span className={`text-[13px] ${syncProgress >= 50 ? 'text-[#0F172A] font-medium' : syncProgress >= 15 ? 'text-[#475569]' : 'text-[#94A3B8]'}`} data-id="element-11812" data-mp="13j09uktoqk1v">
                      Fetching order data
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5" data-id="element-11813" data-mp="1ch2inmjnugzp">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${syncProgress >= 85 ? 'bg-[#10B981]' : syncProgress >= 50 ? 'bg-[#E0F2FE] border-2 border-[#0EA5E9]' : 'bg-[#E2E8F0]'}`} data-id="element-11814" data-mp="3jao3o7dejwgg">
                      {syncProgress >= 85 && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-11815" data-mp="1tuu0szcfx1ff">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-11816" data-mp="1k3g3my7yodpg" />
                        </svg>}
                    </div>
                    <span className={`text-[13px] ${syncProgress >= 85 ? 'text-[#0F172A] font-medium' : syncProgress >= 50 ? 'text-[#475569]' : 'text-[#94A3B8]'}`} data-id="element-11817" data-mp="1ozoe3yb7riqa">
                      Processing & organizing
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5" data-id="element-11818" data-mp="114vevctjlkez">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${syncProgress >= 100 ? 'bg-[#10B981]' : syncProgress >= 85 ? 'bg-[#E0F2FE] border-2 border-[#0EA5E9]' : 'bg-[#E2E8F0]'}`} data-id="element-11819" data-mp="162wutovo93m8">
                      {syncProgress >= 100 && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-11820" data-mp="30geeh0syhgbz">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-11821" data-mp="5icorva15mho" />
                        </svg>}
                    </div>
                    <span className={`text-[13px] ${syncProgress >= 100 ? 'text-[#0F172A] font-medium' : syncProgress >= 85 ? 'text-[#475569]' : 'text-[#94A3B8]'}`} data-id="element-11822" data-mp="ejsx7xhabej9">
                      Sync complete
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-[12px] text-[#94A3B8]" data-id="element-11823" data-mp="t8p5nvn96tke">
                You can navigate away — syncing continues in the background
              </p>
            </div>
          </div> : <div className="flex flex-col items-center justify-center min-h-[60vh] p-6" data-id="element-11824" data-mp="12a6lre0nuv93">
            <div className="max-w-md text-center w-full" data-id="element-11825" data-mp="2z2q9o0o4uu08">
              <div className="mb-6 flex justify-center" data-id="element-11826" data-mp="2z49qpthm5932">
                <div className="w-20 h-20 rounded-full bg-[#E0F2FE] flex items-center justify-center" data-id="element-11827" data-mp="3419ufxqzpzr0">
                  <ShoppingCart className="w-10 h-10 text-[#0EA5E9]" data-id="element-11828" data-mp="2wik2lk482nfg" />
                </div>
              </div>
              <h1 className="text-[24px] font-semibold text-[#0F172A] mb-3" data-id="element-11829" data-mp="2rqitu869jeu6">
                Orders Not Yet Synced
              </h1>
              <p className="text-[14px] text-[#64748B] mb-8 leading-relaxed" data-id="element-11830" data-mp="2msqvowf0ywhj">
                Your Shopify store is connected. Sync your orders to start
                viewing and managing them here.
              </p>

              <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm mb-8 text-left" data-id="element-11831" data-mp="3ai1x8g478uwq">
                <p className="text-[13px] text-[#475569] font-medium mb-3" data-id="element-11832" data-mp="35iypgnwu4al2">
                  Once synced, you'll be able to:
                </p>
                <ul className="text-[13px] text-[#64748B] space-y-2" data-id="element-11833" data-mp="31gln89haqut1">
                  <li className="flex items-start gap-2" data-id="element-11834" data-mp="1u4bhgc3pwi79">
                    <span className="text-[#0EA5E9] mt-0.5" data-id="element-11835" data-mp="1u1p94a905ynz">•</span>
                    View and manage all orders
                  </li>
                  <li className="flex items-start gap-2" data-id="element-11836" data-mp="1riua45pklm04">
                    <span className="text-[#0EA5E9] mt-0.5" data-id="element-11837" data-mp="1rhapy40smqt2">•</span>
                    Track payment and fulfillment status
                  </li>
                  <li className="flex items-start gap-2" data-id="element-11838" data-mp="1hlzbznx7lnmd">
                    <span className="text-[#0EA5E9] mt-0.5" data-id="element-11839" data-mp="1hnk5da7sf0in">•</span>
                    Export order data and reports
                  </li>
                </ul>
              </div>

              <Button variant="primary" onClick={() => setIsSyncing(true)} iconLeft={<RefreshCw className="w-4 h-4" data-id="element-11841" data-mp="1dknt018uc0n5" />} className="w-full justify-center" data-id="element-11840" data-mp="18rkx3mglcf3s">
                Sync Orders
              </Button>
            </div>
          </div> : <>
          {/* Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-4" data-id="element-11842" data-mp="13uuhf08p7liw">
            <StatCard label="Total Orders" value={stats.totalOrders} trend={<TrendIndicator value={12} label="vs last period" direction="up" data-id="element-11844" data-mp="tvlqkg94qn0r" />} onClick={handleTotalOrdersClick} data-id="element-11843" data-mp="pv2x6hrl98wg" />
            <StatCard label="Revenue" value={`$${stats.totalRevenue.toLocaleString('en-US', {
          minimumFractionDigits: 2
        })}`} trend={<TrendIndicator value={8} label="vs last period" direction="up" data-id="element-11846" data-mp="69szem1nam49" />} onClick={handleRevenueClick} data-id="element-11845" data-mp="b6t4atmrdits" />
            <StatCard label="Payment Failed" value={stats.paymentFailed} secondaryValue="Requires attention" badge={stats.paymentFailed > 0 ? {
          text: 'Action Needed',
          variant: 'error'
        } : undefined} onClick={handlePaymentFailedClick} data-id="element-11847" data-mp="k9ashdlz4ljf" />
            <StatCard label="Pending Fulfillment" value={stats.pendingFulfillment} secondaryValue="Ready to ship" badge={stats.pendingFulfillment > 0 ? {
          text: 'Action Needed',
          variant: 'warning'
        } : undefined} data-id="element-11848" data-mp="p7vr58lkux5r" />
          </div>

          {/* Date Range Tabs */}
          <div className="flex justify-start mb-6 md:mb-8" data-id="element-11849" data-mp="1cvvjgcyjevmg">
            <div className="bg-[#E2E8F0] p-1 rounded-lg inline-flex" data-id="element-11850" data-mp="1cuc1mhx8gcik">
              {statsTimeOptions.map(option => <button key={option} onClick={() => setStatsTimeRange(option)} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${statsTimeRange === option ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#475569]'}`} data-id="element-11851" data-mp="17vr1sjlw7uun">
                  {option}
                </button>)}
            </div>
          </div>

          <hr className="my-6 md:my-10 border-[#CBD5E1]" data-id="element-11852" data-mp="25ps1dy3cod8s" />

          {/* Filter Bar */}
          <div className="bg-white border border-[#E2E8F0] rounded-lg mb-4 md:mb-6 p-4 md:p-6 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-11853" data-mp="191vr1ayiae60">
            <div className="flex flex-col gap-4" data-id="element-11854" data-mp="1d4ic8t0o90u8">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-id="element-11855" data-mp="1i192uaa3retw">
                <div className="w-full lg:w-80 flex-shrink-0" data-id="element-11856" data-mp="1n0vhmo5kx34t">
                  <SearchBar placeholder="Search by order #, customer name, or email" value={searchQuery} onChange={setSearchQuery} className="w-full" data-id="element-11857" data-mp="1my90bwsvz99e" />
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-auto" data-id="element-11858" data-mp="2ydxlqjqhs083">
                  <ExportButton onExport={async type => {
                console.log(`Exporting ${type}`);
              }} data-id="element-11859" data-mp="mr3vf26jrop5" />
                  <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} onCustomDateChange={handleCustomDateChange} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyButton={true} data-id="element-11860" data-mp="hvf41m4ole3x" />
                </div>
              </div>
              {addedFilters.size > 0 && <div className="flex items-center gap-3" data-id="element-11861" data-mp="3v7fhx5dcesrb">
                  <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} onCustomDateChange={handleCustomDateChange} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyChips={true} data-id="element-11862" data-mp="3q8vrarnt0ssm" />
                  <button onClick={handleClearAll} className="text-[13px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors" data-id="element-11863" data-mp="1ji63tu1zzzzs">
                    Clear all
                  </button>
                </div>}
            </div>
          </div>

          {/* Orders Table */}
          {filteredOrders.length > 0 ? <>
              <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden mb-4 md:mb-6 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-11864" data-mp="3421h5b5wb6yt">
                <div className="overflow-x-auto" data-id="element-11865" data-mp="38xgqbpsykeow">
                  <table className="w-full" data-id="element-11866" data-mp="l1c6j7ajcfa">
                    <thead className="bg-[#F1F5F9]" data-id="element-11867" data-mp="i6w13vb8dro">
                      <tr data-id="element-11868" data-mp="5hru7c2noi3c">
                        <th className="px-3 md:px-4 py-3 text-left" data-id="element-11869" data-mp="5ibakocnqyf9">
                          <button onClick={() => handleSort('id')} className="flex items-center gap-2 text-xs font-semibold text-[#475569] uppercase tracking-wider hover:text-[#0EA5E9] transition-colors" data-id="element-11870" data-mp="3nnxsxchbxand">
                            Order #
                            {sortConfig?.key === 'id' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-11871" data-mp="2gbxcd0gz1168" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-11872" data-mp="28rmr50jmkbye" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-11873" data-mp="28q1wlaxb8t0j" />}
                          </button>
                        </th>
                        <th className="px-3 md:px-4 py-3 text-left" data-id="element-11874" data-mp="2ipsw5r0q9y7h">
                          <button onClick={() => handleSort('customer')} className="flex items-center gap-2 text-xs font-semibold text-[#64748B] uppercase tracking-wider hover:text-[#0EA5E9] transition-colors" data-id="element-11875" data-mp="2ip160vpwgsol">
                            Customer
                            {sortConfig?.key === 'customer' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-11876" data-mp="1l07mh14dlijz" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-11877" data-mp="1zrzyb34yoegy" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-11878" data-mp="1v08ah0d6wmui" />}
                          </button>
                        </th>
                        <th className="px-3 md:px-4 py-3 text-left" data-id="element-11879" data-mp="179cleb04c5qy">
                          <button onClick={() => handleSort('type')} className="flex items-center gap-2 text-xs font-semibold text-[#64748B] uppercase tracking-wider hover:text-[#0EA5E9] transition-colors" data-id="element-11880" data-mp="12c2ns82ksns1">
                            Type
                            {sortConfig?.key === 'type' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-11881" data-mp="qpw2czjtv9um" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-11882" data-mp="qp493vdpdo7i" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-11883" data-mp="us96jjixezpa" />}
                          </button>
                        </th>
                        <th className="px-3 md:px-4 py-3 text-left" data-id="element-11884" data-mp="zozn07yn7xb8">
                          <button onClick={() => handleSort('date')} className="flex items-center gap-2 text-xs font-semibold text-[#64748B] uppercase tracking-wider hover:text-[#0EA5E9] transition-colors" data-id="element-11885" data-mp="14ovs6aj2tusd">
                            Date
                            {sortConfig?.key === 'date' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-11886" data-mp="1iiwfm84kvvdf" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-11887" data-mp="1ngobfd8cg1mw" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-11888" data-mp="1nh7q8l2p69wp" />}
                          </button>
                        </th>
                        <th className="px-3 md:px-4 py-3 text-left" data-id="element-11889" data-mp="32zvwy6g6unqn">
                          <button onClick={() => handleSort('items')} className="flex items-center gap-2 text-xs font-semibold text-[#64748B] uppercase tracking-wider hover:text-[#0EA5E9] transition-colors" data-id="element-11890" data-mp="331qjtqb0spxd">
                            Items
                            {sortConfig?.key === 'items' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-11891" data-mp="3qrb6aj6du5be" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-11892" data-mp="3moo69uzk5e34" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-11893" data-mp="3mpzce1gh6xxe" />}
                          </button>
                        </th>
                        <th className="px-3 md:px-4 py-3 text-left" data-id="element-11894" data-mp="3cwqvgpcgoo4x">
                          <button onClick={() => handleSort('total')} className="flex items-center gap-2 text-xs font-semibold text-[#64748B] uppercase tracking-wider hover:text-[#0EA5E9] transition-colors" data-id="element-11895" data-mp="e7jobmz976y4">
                            Total
                            {sortConfig?.key === 'total' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-11896" data-mp="21c090jx3qy12" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-11897" data-mp="21dbm5acfti4u" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-11898" data-mp="1wl0c3enzgfyk" />}
                          </button>
                        </th>
                        <th className="px-3 md:px-4 py-3 text-left" data-id="element-11899" data-mp="1rmgqxgh7r879">
                          <button onClick={() => handleSort('paymentStatus')} className="flex items-center gap-2 text-xs font-semibold text-[#64748B] uppercase tracking-wider hover:text-[#0EA5E9] transition-colors" data-id="element-11900" data-mp="1moov4bdg71xs">
                            Payment
                            {sortConfig?.key === 'paymentStatus' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-11901" data-mp="18pp9lcl8f35u" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-11902" data-mp="381i9bgzemmm9" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-11903" data-mp="37yw2xko92d5m" />}
                          </button>
                        </th>
                        <th className="px-3 md:px-4 py-3 text-left" data-id="element-11904" data-mp="3alws3m3pbsgn">
                          <button onClick={() => handleSort('fulfillmentStatus')} className="flex items-center gap-2 text-xs font-semibold text-[#64748B] uppercase tracking-wider hover:text-[#0EA5E9] transition-colors" data-id="element-11905" data-mp="3ajk8l4iapa1h">
                            Fulfillment
                            {sortConfig?.key === 'fulfillmentStatus' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-11906" data-mp="2rnc5a68uf98t" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-11907" data-mp="2ron3lqjirosj" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-11908" data-mp="2wmon2ckuxq4j" />}
                          </button>
                        </th>
                        <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-12" data-id="element-11909" data-mp="31n2ym4lo3i7b">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]" data-id="element-11910" data-mp="kv1jd7xlqdcu">
                      {paginatedOrders.map(order => <tr key={order.id} className={`hover:bg-[#F8FAFC] transition-colors duration-200 ease-in-out ${order.paymentStatus === 'Failed' ? 'border-l-4 border-l-[#EF4444]' : ''}`} data-id="element-11911" data-mp="2hvnurze07f9k">
                          <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-11912" data-mp="2qxna8n9debae">
                            <button className="font-mono text-sm text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors duration-200 ease-in-out" data-id="element-11913" data-mp="2lyaoy6y5dc9x">
                              #{order.id}
                            </button>
                          </td>
                          <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-11914" data-mp="1u8z870e1uymm">
                            <div data-id="element-11915" data-mp="27zkrgq7f1al1">
                              <div className="text-sm font-medium text-[#0F172A]" data-id="element-11916" data-mp="280m7za77apa5">
                                {order.customerName}
                              </div>
                              <div className="text-[13px] text-[#94A3B8] mt-0.5" data-id="element-11917" data-mp="1fgxc7rf3oprv">
                                {order.customerEmail}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-11918" data-mp="3m4nz1sypr34y">
                            <Badge variant={order.type === 'Subscription' ? 'info' : 'default'} data-id="element-11919" data-mp="3qys58a05n74z">
                              {order.type}
                            </Badge>
                          </td>
                          <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-11920" data-mp="3i55eiyat0u2n">
                            <div className="text-sm text-[#0F172A]" data-id="element-11921" data-mp="9q573r2wmk6w">
                              {formatDate(order.date)}
                            </div>
                            <div className="text-xs text-[#94A3B8] mt-0.5" data-id="element-11922" data-mp="3vwaiudp4aajb">
                              {formatTime(order.date)}
                            </div>
                          </td>
                          <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-11923" data-mp="4rtz7lw66jrb">
                            <span className="text-sm text-[#475569]" data-id="element-11924" data-mp="2kfk9wmtb74m0">
                              {order.items} items
                            </span>
                          </td>
                          <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-11925" data-mp="34qodonwbu7xk">
                            <span className="text-sm font-semibold text-[#0F172A]" data-id="element-11926" data-mp="2zymtmfsx3cwz">
                              ${order.total.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-11927" data-mp="2qz9izjt24iao">
                            {order.paymentStatus === 'In Retry' && order.retryCount && order.maxRetries ? <Badge variant="warning" data-id="element-11928" data-mp="3nmmwge3i6trx">
                                In Retry ({order.retryCount}/{order.maxRetries})
                              </Badge> : <Badge variant={getPaymentStatusVariant(order.paymentStatus)} data-id="element-11929" data-mp="3enj9gz17tu85">
                                {order.paymentStatus}
                              </Badge>}
                          </td>
                          <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-11930" data-mp="239xbqphaikb0">
                            <Badge variant={getFulfillmentStatusVariant(order.fulfillmentStatus)} data-id="element-11931" data-mp="1ya2kgktlrspf">
                              {order.fulfillmentStatus}
                            </Badge>
                          </td>
                          <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-11932" data-mp="1t45x6ap82lmy">
                            <div className="relative" data-id="element-11933" data-mp="1y420s8u0cb1z">
                              <button onClick={() => setOpenOrderDropdown(openOrderDropdown === order.id ? null : order.id)} className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors duration-200 ease-in-out" data-id="element-11934" data-mp="1y2h68j7p0s44">
                                <MoreVertical className="w-5 h-5 text-[#64748B]" data-id="element-11935" data-mp="26ygcy2j8r5gv" />
                              </button>
                              {openOrderDropdown === order.id && <>
                                  <div className="fixed inset-0 z-10" onClick={() => setOpenOrderDropdown(null)} data-id="element-11936" data-mp="2gvb7ybgb2nqe" />
                                  <div className="absolute right-0 mt-2 w-56 bg-white border border-[#CBD5E1] rounded-md shadow-lg z-20 py-1" data-id="element-11937" data-mp="2lra4wnfp9e2f">
                                    <button onClick={() => handleViewOrderInShopify(order.id)} className="w-full px-4 py-2 text-left text-sm hover:bg-[#F8FAFC] transition-colors text-[#0F172A] flex items-center gap-2" data-id="element-11938" data-mp="retrn2ohkzo2">
                                      <ExternalLink className="w-4 h-4" data-id="element-11939" data-mp="eh49z6ldwdco" />
                                      View Order in Shopify
                                    </button>
                                  </div>
                                </>}
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-11940" data-mp="1c44cqt2kzmvk">
                <div className="text-[13px] text-[#64748B]" data-id="element-11941" data-mp="173yid54xfm9s">
                  Showing {(currentPage_ - 1) * pageSize + 1}-
                  {Math.min(currentPage_ * pageSize, filteredOrders.length)} of{' '}
                  {filteredOrders.length} orders
                </div>
                <div className="flex items-center gap-2" data-id="element-11942" data-mp="1zqykt8drj4hp">
                  <button onClick={() => setCurrentPage_(Math.max(1, currentPage_ - 1))} disabled={currentPage_ === 1} className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 ease-in-out" data-id="element-11943" data-mp="1zrhrfsxyq6gf">
                    Previous
                  </button>
                  <div className="flex items-center gap-1" data-id="element-11944" data-mp="1gzfh0zbwzmem">
                    {Array.from({
                length: totalPages
              }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage_(page)} className={`w-10 h-10 rounded-md text-[13px] font-medium transition-colors duration-200 ease-in-out ${page === currentPage_ ? 'bg-[#0EA5E9] text-white' : 'text-[#475569] hover:bg-[#F8FAFC]'}`} data-id="element-11945" data-mp="17zsg0en3kim3">
                        {page}
                      </button>)}
                  </div>
                  <button onClick={() => setCurrentPage_(Math.min(totalPages, currentPage_ + 1))} disabled={currentPage_ === totalPages} className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 ease-in-out" data-id="element-11946" data-mp="13062e43cx0cr">
                    Next
                  </button>
                </div>
              </div>
            </> : <>
              <div className="bg-white rounded-lg border border-[#E2E8F0] p-8 md:p-12 text-center shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-11947" data-mp="zjl3buvxfwt4">
                <Package className="w-12 h-12 md:w-16 md:h-16 text-[#CBD5E1] mx-auto mb-4" data-id="element-11948" data-mp="usct4wmrw7ew" />
                <h3 className="text-lg md:text-xl font-semibold text-[#0F172A] mb-2" data-id="element-11949" data-mp="puuqtp39gqfv">
                  No orders found
                </h3>
                <p className="text-sm md:text-base text-[#64748B] mb-6" data-id="element-11950" data-mp="lqn0xol2h4g2">
                  Try adjusting your filters or date range
                </p>
                {hasActiveFilters && <Button variant="outline" onClick={handleClearAll} data-id="element-11951" data-mp="busa466ow327">
                    Clear all filters
                  </Button>}
              </div>
            </>}
        </>}
    </div>;
}